export interface Reservoir {
  add(value: number): void;
  quantile(q: number): number | null;
  max(): number | null;
  /** Values offered, not values kept — the two differ once the capacity is reached. */
  count(): number;
}

/**
 * Fixed-size sample of an unbounded stream (Vitter's algorithm R).
 *
 * A long visit can fire tens of thousands of pointer events. Keeping them all would cost memory on
 * the device and bytes on the wire for a distribution that a bounded sample already describes, and
 * storing the values themselves would turn a set of counters into a recording of what happened on
 * screen. Only the shape survives.
 */
export const createReservoir = (capacity = 128): Reservoir => {
  const kept: number[] = [];
  let seen = 0;
  let largest: number | null = null;

  return {
    add(value: number): void {
      if (!Number.isFinite(value)) return;
      seen += 1;
      if (largest === null || value > largest) largest = value;
      if (kept.length < capacity) {
        kept.push(value);
        return;
      }
      const slot = Math.floor(Math.random() * seen);
      if (slot < capacity) kept[slot] = value;
    },

    // Discrete quantile by nearest rank — the same definition PostgreSQL's `percentile_disc` uses.
    // The baseline report takes quantiles of these numbers, so the two definitions have to agree or
    // the aggregate drifts against its own inputs.
    quantile(q: number): number | null {
      if (kept.length === 0) return null;
      const sorted = [...kept].sort((a, b) => a - b);
      const rank = Math.ceil(q * sorted.length) - 1;
      return sorted[Math.min(sorted.length - 1, Math.max(0, rank))];
    },

    max(): number | null {
      return largest;
    },

    count(): number {
      return seen;
    },
  };
};
